import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { LEVEL_LABEL } from '@app/shared';
import type { UserLevel, UserOption } from '@app/shared';
import { useAuth } from '../features/auth/AuthProvider';
import { api } from '../lib/api';

const CHAIN: UserLevel[] = ['master', 'distributor', 'retailer', 'employee'];

export type AgentChain = Partial<Record<UserLevel, string>>;

/** The most specific user chosen in the chain, or undefined when nothing is selected. */
export const deepestAgent = (chain: AgentChain): string | undefined => {
  for (let i = CHAIN.length - 1; i >= 0; i -= 1) {
    const id = chain[CHAIN[i]!];
    if (id) return id;
  }
  return undefined;
};

function LevelSelect(props: {
  level: UserLevel;
  parentId?: string;
  enabled: boolean;
  value: string;
  onChange: (id: string) => void;
}) {
  const { level, parentId, enabled } = props;
  const options = useQuery({
    queryKey: ['users', 'options', level, parentId ?? null],
    queryFn: () => api.get<{ data: UserOption[] }>('/users/options', { level, parentId }),
    enabled,
    select: (r) => r.data,
  });
  const id = `agent-${level}`;
  return (
    <FormControl disabled={!enabled}>
      <InputLabel id={`${id}-label`}>{LEVEL_LABEL[level]}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        label={LEVEL_LABEL[level]}
        value={options.data?.some((o) => o.id === props.value) ? props.value : ''}
        onChange={(e) => props.onChange(e.target.value)}
      >
        <MenuItem value="">
          <em>--Select--</em>
        </MenuItem>
        {options.data?.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            {o.name} ({o.loginId})
          </MenuItem>
        ))}
      </Select>
      {options.isError ? <FormHelperText error>Could not load options</FormHelperText> : null}
    </FormControl>
  );
}

interface AgentChainSelectProps {
  value: AgentChain;
  onChange: (value: AgentChain) => void;
  /** Deepest level to show. Defaults to employee. */
  through?: UserLevel;
}

/**
 * Cascading Master agent > Distributor > Retailer > Employee selects, as used on the
 * reference's inventory and report filters. Each select lists only users the signed-in
 * user may see; levels above the signed-in user are not shown, and each select below
 * the top stays disabled until the level above is chosen.
 */
export function AgentChainSelect({ value, onChange, through = 'employee' }: AgentChainSelectProps) {
  const { user } = useAuth();
  const own = user && user.level !== 'admin' ? CHAIN.indexOf(user.level) : -1;
  const last = CHAIN.indexOf(through);
  // A non-admin starts at their own level (which lists just themselves).
  const levels = CHAIN.slice(Math.max(own, 0), last + 1);

  return (
    <>
      {levels.map((level, i) => {
        const parentLevel = i > 0 ? levels[i - 1] : undefined;
        const parentId = parentLevel ? value[parentLevel] : undefined;
        return (
          <LevelSelect
            key={level}
            level={level}
            parentId={parentId}
            enabled={i === 0 || Boolean(parentId)}
            value={value[level] ?? ''}
            onChange={(id) => {
              // Changing a level clears everything below it.
              const next: AgentChain = {};
              for (const l of levels.slice(0, i)) next[l] = value[l];
              if (id) next[level] = id;
              onChange(next);
            }}
          />
        );
      })}
    </>
  );
}
